import React, { useState, useMemo } from 'react';
import { Settings, Eye, EyeOff, Send, Download, ArrowRightLeft, Clock, ArrowUpRight, ArrowDownLeft, Wallet, ChevronDown } from 'lucide-react';
import { useWallet } from '../context/WalletContext';
import DepositModal from '../components/wallet/DepositModal';
import WithdrawModal from '../components/wallet/WithdrawModal';
import TransferModal from '../components/wallet/TransferModal';
import Header from '../components/Header';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const ASSET_CONFIG = {
  BTC: { color: '#f7931a', gradient: ['#f7931a', '#e8820e'], icon: '₿' },
  ETH: { color: '#627eea', gradient: ['#627eea', '#4a66d4'], icon: 'Ξ' },
  SOL: { color: '#9945ff', gradient: ['#9945ff', '#7c3aed'], icon: '◎' },
  USDT: { color: '#26a17b', gradient: ['#26a17b', '#1e8f6c'], icon: '$' },
  USDC: { color: '#2775ca', gradient: ['#2775ca', '#1e5fa8'], icon: '$' },
  BNB: { color: '#f3ba2f', gradient: ['#f3ba2f', '#d4a01a'], icon: 'B' },
  MATIC: { color: '#8247e5', gradient: ['#8247e5', '#6b35c4'], icon: 'M' },
  default: { color: '#6366f1', gradient: ['#6366f1', '#4f46e5'], icon: '?' }
};

// Decorative sparkline path for each asset (consistent per asset)
const SPARKLINE_PATHS = {
  BTC: 'M0,12 Q5,8 10,10 T20,6 T30,9 T40,4 T50,7',
  ETH: 'M0,10 Q5,6 10,8 T20,5 T30,11 T40,3 T50,8',
  SOL: 'M0,8 Q5,14 10,6 T20,12 T30,4 T40,10 T50,5',
  USDT: 'M0,9 Q5,9 10,9 T20,9 T30,9 T40,9 T50,9',
  USDC: 'M0,9 Q5,8 10,10 T20,8 T30,10 T40,9 T50,9',
  BNB: 'M0,11 Q5,7 10,9 T20,5 T30,8 T40,3 T50,6',
  MATIC: 'M0,6 Q5,10 10,4 T20,11 T30,7 T40,9 T50,5',
  default: 'M0,10 Q5,7 10,11 T20,6 T30,9 T40,4 T50,8'
};

// Balance chart decorative path
const BALANCE_CHART_PATH = 'M0,80 C30,75 60,60 90,55 C120,50 140,30 170,25 C200,20 230,35 260,28 C290,21 320,15 350,20 C380,25 400,10 430,8 C460,6 490,12 520,5';

export default function WalletDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filteredWallets, wallets, loading, refreshWallets, selectedWalletType, setSelectedWalletType } = useWallet();
  const [activeModal, setActiveModal] = useState(null);
  const [hideBalances, setHideBalances] = useState(false);

  const walletTypes = [
    { key: 'spot', label: 'Spot', color: '#3b82f6' },
    { key: 'futures', label: 'Futures', color: '#f43f5e' },
    { key: 'investment', label: 'Investment', color: '#a855f7' }
  ];

  const totals = useMemo(() => {
    if (!wallets) return { spot: 0, futures: 0, investment: 0, grand: 0 };
    const r = { spot: 0, futures: 0, investment: 0 };
    wallets.forEach(w => { r[w.wallet_type] = (r[w.wallet_type] || 0) + parseFloat(w.value_usdt || 0); });
    r.grand = r.spot + r.futures + r.investment;
    return r;
  }, [wallets]);

  const selectedBalance = totals[selectedWalletType] || 0;

  const assetList = useMemo(() => {
    const source = filteredWallets || [];
    const map = {};
    source.forEach(w => {
      const v = parseFloat(w.value_usdt || 0);
      const b = parseFloat(w.balance || 0);
      // Afficher tous les wallets avec un solde > 0, y compris la crypto interne
      if (b > 0) {
        if (!map[w.asset]) map[w.asset] = { 
          asset: w.asset, 
          balance: 0, 
          value: 0, 
          is_internal: w.is_internal, 
          is_tradable: w.is_tradable,
          symbol: w.symbol,
          name: w.name
        };
        map[w.asset].balance += b;
        map[w.asset].value += v;
      }
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filteredWallets]);

  const fmt = (v) => hideBalances ? '••••••' : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtSmall = (v) => hideBalances ? '••••' : v.toFixed(2);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060a14] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-t-2 border-blue-500 animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Wallet size={24} className="text-blue-400 animate-pulse" />
            </div>
          </div>
          <p className="text-slate-500 font-semibold text-xs uppercase tracking-[0.3em]">{t('wallet.loading_wallets')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060a14] text-white font-inter">
      <Header />
      <div className="max-w-lg mx-auto px-4 pt-24 pb-28 md:pb-10 space-y-6">

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">{t('nav.wallet')}</h1>
        </div>

        {/* Wallet Type Tabs */}
        <div className="flex gap-2 p-1 bg-[#0c1220] rounded-2xl border border-white/[0.04]">
          {walletTypes.map(wt => (
            <button
              key={wt.key}
              onClick={() => setSelectedWalletType(wt.key)}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                selectedWalletType === wt.key
                  ? 'bg-white/10 text-white shadow-lg'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
              style={selectedWalletType === wt.key ? { boxShadow: `0 0 20px ${wt.color}20` } : {}}
            >
              {wt.label}
            </button>
          ))}
        </div>

        {/* Balance Card */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f1629] via-[#111d35] to-[#0a1225] border border-white/[0.06] p-6 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
          {/* Glow effects */}
          <div className="absolute top-[-50%] right-[-20%] w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute bottom-[-30%] left-[-10%] w-[200px] h-[200px] bg-purple-500/8 rounded-full blur-[60px] pointer-events-none" />

          <div className="relative z-10">
            {/* Total Balance Label */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm font-medium">{t('wallet.total_value_estimated')}</span>
                <button onClick={() => setHideBalances(!hideBalances)} className="text-slate-500 hover:text-slate-300 transition-colors">
                  {hideBalances ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Balance Amount */}
            <div className="mb-1">
              <span className="text-4xl font-bold text-white tabular-nums tracking-tight">
                {hideBalances ? '••••••' : `$${selectedBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </span>
            </div>

            {/* Change */}
            <div className="flex items-center gap-2 mb-5">
              <span className="text-emerald-400 text-sm font-semibold flex items-center gap-1">
                <ArrowUpRight size={14} /> +2.4%
              </span>
              <span className="text-slate-500 text-xs">(24h)</span>
            </div>

            {/* Chart */}
            <div className="relative h-28 -mx-2 overflow-hidden">
              <svg viewBox="0 0 350 100" className="w-full h-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                    <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.1" />
                    <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="50%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Area fill */}
                <path d={`${BALANCE_CHART_PATH} L350,100 L0,100 Z`} fill="url(#chartGradient)" />
                {/* Glowing line */}
                <path d={BALANCE_CHART_PATH} fill="none" stroke="url(#lineGradient)" strokeWidth="2.5" filter="url(#glow)" />
                {/* End dot */}
                <circle cx="350" cy="15" r="4" fill="#06b6d4" filter="url(#glow)" />
                <circle cx="350" cy="15" r="2" fill="white" />
              </svg>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: <ArrowUpRight size={20} />, label: t('wallet.action_withdraw'), color: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-500/30', action: 'withdraw' },
            { icon: <Download size={20} />, label: t('wallet.action_deposit'), color: 'from-purple-500 to-purple-600', shadow: 'shadow-purple-500/30', action: 'deposit' },
            { icon: <ArrowRightLeft size={20} />, label: t('wallet.action_transfer') || 'Transfer', color: 'from-emerald-500 to-emerald-600', shadow: 'shadow-emerald-500/30', action: 'transfer' },
            { icon: <Download size={20} />, label: t('wallet.invest_label') || 'Invest', color: 'from-orange-400 to-orange-500', shadow: 'shadow-orange-400/30', action: 'invest' }
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => { if (item.action === 'invest') navigate('/packs'); else if (item.action) setActiveModal(item.action); }}
              className="flex flex-col items-center gap-2.5 group"
            >
              <div className={`w-13 h-13 rounded-full bg-gradient-to-br ${item.color} ${item.shadow} flex items-center justify-center text-white shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                {item.icon}
              </div>
              <span className="text-[10px] font-semibold text-slate-400 group-hover:text-slate-200 transition-colors">{item.label}</span>
            </button>
          ))}
        </div>

        {/* My Assets */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-white">My Assets</h2>
            <button className="text-blue-400 text-xs font-semibold hover:text-blue-300 transition-colors">See All</button>
          </div>

          <div className="space-y-2">
            {assetList.length > 0 ? assetList.map(({ asset, balance, value, is_internal }) => {
              const config = ASSET_COLORS[asset] || ASSET_CONFIG.default;
              const sparkPath = SPARKLINE_PATHS[asset] || SPARKLINE_PATHS.default;

              return (
                <div
                  key={asset}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-[#0c1220] hover:bg-[#111d30] border border-white/[0.04] hover:border-white/[0.08] transition-all duration-300 cursor-pointer"
                >
                  {/* Icon */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${config.gradient[0]}, ${config.gradient[1]})` }}
                  >
                    {config.icon !== '?' ? config.icon : asset.charAt(0)}
                  </div>

                  {/* Name + Sparkline */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{asset}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-500 font-medium">{balance.toFixed(4)}</span>
                    </div>
                  </div>

                  {/* Mini Sparkline */}
                  <svg viewBox="0 0 50 20" className="w-14 h-6 shrink-0" preserveAspectRatio="none">
                    <path
                      d={sparkPath}
                      fill="none"
                      stroke={config.color}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      opacity="0.6"
                    />
                  </svg>

                  {/* Value */}
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-white tabular-nums">{hideBalances ? '••••' : (value > 0 ? `$${fmt(value)}` : '-')}</p>
                    {is_internal ? (
                      <p className="text-[10px] font-semibold text-purple-400 mt-0.5 border border-purple-500/30 bg-purple-500/10 px-1.5 py-0.5 rounded inline-block">
                        Pre-launch
                      </p>
                    ) : asset !== 'USDT' && asset !== 'USDC' ? (
                      <p className="text-[10px] font-semibold text-emerald-400 mt-0.5">
                        +{(Math.random() * 5 + 0.5).toFixed(2)}%
                      </p>
                    ) : (
                      <p className="text-[10px] font-semibold text-slate-500 mt-0.5">
                        ≈ $1.00
                      </p>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-12 rounded-3xl bg-[#0c1220] border border-white/[0.04]">
                <Wallet size={40} className="text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500 text-sm font-medium mb-4">{t('wallet.no_wallets')}</p>
                <button
                  onClick={() => setActiveModal('deposit')}
                  className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-all"
                >
                  {t('wallet.first_deposit_cta')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Earn Banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f1830] via-[#111d3a] to-[#0d1530] border border-white/[0.06] p-5">
          <div className="absolute top-0 right-0 w-[150px] h-[150px] bg-blue-500/8 rounded-full blur-[60px] pointer-events-none" />
          <div className="flex items-center justify-between relative z-10">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🚀</span>
                <h3 className="text-lg font-bold text-white">Earn up to 12%</h3>
              </div>
              <p className="text-xs text-slate-400 font-medium mb-3">Stake your assets and grow your portfolio</p>
              <button
                onClick={() => navigate('/packs')}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-blue-500/20"
              >
                Stake Now
              </button>
            </div>
            {/* Decorative coins */}
            <div className="flex items-center -space-x-2 ml-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-lg font-bold text-white shadow-lg shadow-amber-500/30 border-2 border-[#111d3a]">₿</div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-sm font-bold text-white shadow-lg shadow-blue-500/30 border-2 border-[#111d3a]">Ξ</div>
            </div>
          </div>
        </div>

      </div>

      {/* Modals */}
      <DepositModal isOpen={activeModal === 'deposit'} onClose={() => { setActiveModal(null); refreshWallets(); }} />
      <WithdrawModal isOpen={activeModal === 'withdraw'} onClose={() => { setActiveModal(null); refreshWallets(); }} />
      <TransferModal isOpen={activeModal === 'transfer'} onClose={() => { setActiveModal(null); refreshWallets(); }} />
    </div>
  );
}

const ASSET_COLORS = {
  BTC: { color: '#f7931a', gradient: ['#f7931a', '#e8820e'], icon: '₿' },
  ETH: { color: '#627eea', gradient: ['#627eea', '#4a66d4'], icon: 'Ξ' },
  SOL: { color: '#9945ff', gradient: ['#9945ff', '#7c3aed'], icon: '◎' },
  USDT: { color: '#26a17b', gradient: ['#26a17b', '#1e8f6c'], icon: '$' },
  USDC: { color: '#2775ca', gradient: ['#2775ca', '#1e5fa8'], icon: '$' },
  BNB: { color: '#f3ba2f', gradient: ['#f3ba2f', '#d4a01a'], icon: 'B' },
  MATIC: { color: '#8247e5', gradient: ['#8247e5', '#6b35c4'], icon: 'M' },
  default: { color: '#6366f1', gradient: ['#6366f1', '#4f46e5'], icon: '?' }
};